import { NavLink, Outlet } from '@remix-run/react'
import { useRef, useState } from 'react'
import { Container } from '#app/components/layout/container.tsx'
import { Flex } from '#app/components/layout/flex'
import { Sticky } from '#app/components/layout/sticky'
import { RemixBreadcrumbs } from '#app/components/navigation/remix-breadcrumbs.tsx'
import { Span } from '#app/components/typography/span.tsx'
import { useIsomorphicLayoutEffect } from '#app/hooks/useIsomorphicLayoutEffect.ts'
import { navigation } from '#app/navigation.ts'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '#app/old_components/ui/collapsible.tsx'
import { Icon } from '#app/old_components/ui/icon.tsx'

const ComponentsRoute = () => {
	return (
		<>
			<Container>
				<RemixBreadcrumbs className="mb-4 align-baseline" />
				<Flex gap="10">
					<Sticky className="top-0 h-full">
						<Sidebar />
					</Sticky>
					<div></div>
				</Flex>
				<Outlet />
			</Container>
		</>
	)
}
export default ComponentsRoute

const Sidebar = () => {
	const sidebarNav = navigation.filter(({ name }) => name === 'Getting Started' || name === 'Components')
	return (
		<Flex orientation="vertical" gap="15" className="min-w-[250px]">
			{sidebarNav.map(({ href, name, children }) => (
				<Flex key={name} orientation="vertical">
					<NavLink to={href} className="text-muted-500 hover:bg-muted-200 -ml-8 rounded-lg p-8 text-xs font-medium">
						{name}
					</NavLink>
					{children?.map(item => <Submenu key={item.href} item={item} />)}
				</Flex>
			))}
		</Flex>
	)
}

type Item =
	| {
			name: string
			href: string
			done: boolean
	  }
	| {
			name: string
			href: string
			done: boolean
			children: {
				name: string
				href: string
				done: boolean
			}[]
	  }

const Submenu = ({ item }: { item: Item }) => {
	const [isOpen, setIsOpen] = useState<boolean>(true)
	const firstRender = useRef(true)

	useIsomorphicLayoutEffect(() => {
		if (firstRender.current) {
			firstRender.current = false
			const preference = localStorage.getItem(item.href)
			setIsOpen(preference === null ? true : preference === 'true')
			return
		}
		localStorage.setItem(item.href, JSON.stringify(isOpen))
	}, [isOpen])

	return (
		<>
			{'children' in item ?
				<Collapsible open={isOpen} onOpenChange={setIsOpen}>
					<Flex items="center" gap="6" className="text-muted-500 hover:bg-muted-200 -ml-8 rounded-lg py-5 px-8">
						<CollapsibleTrigger className="group min-h-tap min-w-tap hover:bg-muted-300 flex items-center justify-center rounded-md">
							<Icon name="chevron-right" className="text-muted-400 h-8 w-8 transition-transform group-data-[state=open]:rotate-90" />
						</CollapsibleTrigger>
						<NavLink to={item.href} className="aria-[current]:text-foreground -ml-2 w-full rounded p-2 text-xs aria-[current]:font-bold">
							{item.name}
						</NavLink>
					</Flex>
					<CollapsibleContent className="flex flex-col">{item.children?.map(subitem => <BoldNavLink key={subitem.href} {...subitem} />)}</CollapsibleContent>
				</Collapsible>
			:	<BoldNavLink key={item.href} {...item} />}
		</>
	)
}

type BoldNavLinkProps = {
	name: string
	href: string
	done: boolean
}

const BoldNavLink = ({ name, href, done, ...props }: BoldNavLinkProps) => {
	return done ?
			<NavLink to={href} className="hover:bg-muted-200 pl-13 text-muted-500 rounded-md py-5 text-xs" {...props}>
				{name}
			</NavLink>
		:	<Span className="text-muted-500 pl-13 cursor-not-allowed rounded-md py-5 text-xs" {...props}>
				{name} <span className="bg-muted-200 text-muted-400 ml-1 rounded-md py-2 px-3 text-xs">SOON</span>
			</Span>
}
